import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useForm, type FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { Wizard } from "@/components/ui/wizard";
import type { WizardStep } from "@/components/ui/wizard/types";
import { ConfirmDialog } from "@/components/ui/dialog/confirm-dialog";
import { toast } from "@/components/ui/toast";
import { PROTECTED_ROUTES } from "@/utils/navigation";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";
import { getApiErrorMessages } from "@/utils/api-error";
import { stripSpaces } from "@/utils/input-masks";
import {
  StaffMemberCreationSchema,
  type StaffMemberCreationFormData,
} from "@/schemas/staff-member";
import {
  PersonalDataStep,
  ClinicalDataStep,
  RoleLinkStep,
  FinancialStep,
  ReviewStep,
} from "@/components/create-staff-member/steps";
import { EMPLOYMENT_TYPE } from "@/constants/staff";
import { ID_DOCUMENT_TYPE } from "@/constants/athlete";
import { useCreateStaffMember } from "@/queries/use-staff-members";
import { useGyms } from "@/queries/use-gyms";
import type {
  CreateStaffMemberRequestData,
  StaffAddressRequestData,
  StaffGymAssignmentRequestData,
} from "@/api/staff-members/types";

export const Route = createFileRoute("/(protected)/staff/new/")({
  component: RouteComponent,
  // Each entry point seeds the first gym assignment with what it already knows:
  // the staff list of a gym passes that gym, a role-scoped page passes that role.
  // Keeping it in the URL means a new entry point only has to navigate with it.
  validateSearch: (search: Record<string, unknown>) => ({
    gymId: search.gymId !== undefined ? String(search.gymId) : undefined,
    roleId: search.roleId !== undefined ? String(search.roleId) : undefined,
  }),
});

const PERSONAL_DATA_FIELDS: FieldPath<StaffMemberCreationFormData>[] = [
  "firstName",
  "lastName",
  "email",
  "birthdate",
  "phoneNumber",
  "landlineNumber",
  "gender",
  "maritalStatus",
  "nationality",
  "idDocumentType",
  "idDocumentNumber",
  "idDocumentExpirationDate",
  "addressLine1",
  "city",
  "postalCode",
];

const CLINICAL_DATA_FIELDS: FieldPath<StaffMemberCreationFormData>[] = [
  "emergencyContactName",
  "emergencyContactPhoneNumber",
];

const ROLE_LINK_FIELDS: FieldPath<StaffMemberCreationFormData>[] = [
  "disciplineIds",
  "employmentType",
  "gymAssignments",
  "primaryAssignmentIndex",
];

const FINANCIAL_FIELDS: FieldPath<StaffMemberCreationFormData>[] = [
  "fiscalFullName",
  "taxId",
  "contractStartDate",
  "contractEndDate",
  "iban",
  "billingAddressLine1",
  "billingCity",
  "billingPostalCode",
];

const DEFAULT_VALUES: StaffMemberCreationFormData = {
  avatar: "",
  firstName: "",
  lastName: "",
  email: "",
  birthdate: "",
  phoneNumber: "",
  landlineNumber: "",
  gender: "",
  maritalStatus: "",
  nationality: "",
  idDocumentType: ID_DOCUMENT_TYPE.CitizenCard,
  idDocumentNumber: "",
  idDocumentExpirationDate: "",
  emergencyContactName: "",
  emergencyContactPhoneNumber: "",
  emergencyContactRelationship: "",
  medicalConditions: "",
  allergiesIntolerances: "",
  currentMedication: "",
  disciplineIds: [],
  employmentType: EMPLOYMENT_TYPE.Contract,
  gymAssignments: [],
  primaryAssignmentIndex: 0,
  addressLine1: "",
  addressLine2: "",
  city: "",
  postalCode: "",
  fiscalFullName: "",
  taxId: "",
  contractStartDate: "",
  contractEndDate: "",
  iban: "",
  billingAddressSameAsMain: true,
  billingAddressLine1: "",
  billingAddressLine2: "",
  billingCity: "",
  billingPostalCode: "",
};

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { gymId, roleId } = Route.useSearch();
  const { mutate: createStaffMember, isPending } = useCreateStaffMember();
  const { data: gymsData } = useGyms();

  const form = useForm<StaffMemberCreationFormData>({
    resolver: zodResolver(StaffMemberCreationSchema()),
    mode: "onSubmit",
    defaultValues: {
      ...DEFAULT_VALUES,
      gymAssignments: [{ gymId: gymId ?? "", roleIds: roleId ? [roleId] : [] }],
    },
  });

  const steps: WizardStep<StaffMemberCreationFormData>[] = useMemo(
    () => [
      {
        id: "personal-data",
        label: t("pages:staff.wizard.steps.personalData"),
        component: PersonalDataStep,
        fields: PERSONAL_DATA_FIELDS,
      },
      {
        id: "clinical-data",
        label: t("pages:staff.wizard.steps.clinicalData"),
        component: ClinicalDataStep,
        fields: CLINICAL_DATA_FIELDS,
      },
      {
        id: "role-link",
        label: t("pages:staff.wizard.steps.roleLink"),
        component: RoleLinkStep,
        fields: ROLE_LINK_FIELDS,
      },
      {
        id: "financial",
        label: t("pages:staff.wizard.steps.financialDetails"),
        component: FinancialStep,
        fields: FINANCIAL_FIELDS,
      },
      {
        id: "confirmation",
        label: t("pages:staff.wizard.steps.confirmation"),
        component: ReviewStep,
      },
    ],
    [t],
  );

  const navigateBack = () => {
    navigate({ to: PROTECTED_ROUTES.STAFF });
  };

  const { handleCancelEdit, unsavedChangesDialogProps } = useUnsavedChanges({
    isEditing: true,
    isDirty: form.formState.isDirty,
    onLeave: navigateBack,
  });

  const handleComplete = (data: StaffMemberCreationFormData) => {
    const personalAddress: StaffAddressRequestData = {
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2 || undefined,
      city: data.city,
      country: "PT",
      postalCode: data.postalCode,
    };

    // The contract always carries a billing address, so mirroring the personal
    // one is a copy rather than an omission.
    const billingAddress: StaffAddressRequestData =
      data.billingAddressSameAsMain
        ? personalAddress
        : {
            addressLine1: data.billingAddressLine1 ?? "",
            addressLine2: data.billingAddressLine2 || undefined,
            city: data.billingCity ?? "",
            country: "PT",
            postalCode: data.billingPostalCode ?? "",
          };

    // One entry per (gym, role) pair, keyed by index as the API expects.
    const gyms = Object.fromEntries(
      data.gymAssignments
        .flatMap<StaffGymAssignmentRequestData>((assignment) =>
          assignment.roleIds.map((assignedRoleId) => ({
            id: assignment.gymId,
            staffRoleId: assignedRoleId,
          })),
        )
        .map((entry, index) => [String(index), entry]),
    );

    const primaryGymId = data.gymAssignments[data.primaryAssignmentIndex].gymId;

    // The IBAN is validated ignoring spaces and casing, so normalise it here
    // to send the API the same value the validation accepted.
    const iban = stripSpaces(data.iban)?.toUpperCase();

    const payload: CreateStaffMemberRequestData = {
      staffMember: {
        hiredAt: data.contractStartDate,
        userIdentity: {
          email: data.email,
        },
        userProfile: {
          firstName: data.firstName,
          lastName: data.lastName,
          birthdate: data.birthdate,
          phoneNumber: stripSpaces(data.phoneNumber) ?? "",
          landlineNumber: stripSpaces(data.landlineNumber),
          gender: data.gender,
          maritalStatus: data.maritalStatus,
          nationality: data.nationality,
          idDocumentType: data.idDocumentType,
          idDocumentNumber: data.idDocumentNumber,
          idDocumentExpirationDate: data.idDocumentExpirationDate,
          avatar: data.avatar,
        },
        addresses: {
          "0": { ...personalAddress, main: true },
        },
        gyms,
        defaultGym: { id: primaryGymId },
        disciplineIds: data.disciplineIds,
        emergencyContact: {
          name: data.emergencyContactName,
          phone: stripSpaces(data.emergencyContactPhoneNumber) ?? "",
          relationship: data.emergencyContactRelationship || undefined,
        },
        healthConsideration: {
          medicalConditions: data.medicalConditions || undefined,
          allergiesIntolerances: data.allergiesIntolerances || undefined,
          currentMedication: data.currentMedication || undefined,
        },
        contract: {
          employmentType: data.employmentType,
          contractStartDate: data.contractStartDate,
          contractEndDate: data.contractEndDate || undefined,
        },
        billingDetail: {
          taxId: data.taxId,
          fiscalFullName: data.fiscalFullName,
          iban: iban || undefined,
          billingAddress,
        },
      },
    };

    createStaffMember(payload, {
      onSuccess: () => {
        const primaryGymName = (gymsData?.gyms ?? []).find(
          (gym) => gym.id.toString() === primaryGymId,
        )?.name;

        toast.success(t("pages:staff.wizard.toaster.success.title"), {
          description: t("pages:staff.wizard.toaster.success.description", {
            gym: primaryGymName,
          }),
        });
        navigateBack();
      },
      onError: (error) => {
        toast.error(t("pages:staff.wizard.toaster.error.title"), {
          description: getApiErrorMessages(error),
        });
      },
    });
  };

  const handleCancel = () => {
    handleCancelEdit(navigateBack);
  };

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>{t("pages:staff.wizard.title")}</MobilePageTitle>
      <PageContainer
        className="flex min-h-0 min-w-[309px] flex-1 flex-col p-0 md:m-0 lg:mb-0"
        internalScroll
      >
        <Wizard
          form={form}
          steps={steps}
          onComplete={handleComplete}
          isSubmitting={isPending}
          onCancel={handleCancel}
        />
      </PageContainer>
      <ConfirmDialog {...unsavedChangesDialogProps} />
    </div>
  );
}
